import { makeObservable, action, observable } from 'mobx';
import { ViewTypes } from '../models/SheetModel';

export class SheetViewModel {
    MAX_SHEETS = 15;
    isViewRequirementsModalOpen = false;
    currentRequirements = null;
    isAddSheetModalOpen = false;

    constructor({ sheetStore }) {
        if (!sheetStore) {
            throw new Error('SheetViewModel requires a sheetStore');
        }
        
        this.stores = { sheetStore };
        makeObservable(this, {
            stores: false,
            isViewRequirementsModalOpen: observable,
            currentRequirements: observable,
            showViewRequirementsModal: action,
            hideViewRequirementsModal: action,
            isAddSheetModalOpen: observable,
            showAddSheetModal: action,
            hideAddSheetModal: action,
            handleAddSheetSelection: action,
            _addBlankSheet: action,
            _addSheetFromViewType: action,
            _addSheetFromTemplate: action,
            selectSheet: action,
            removeSheet: action,
            updateSheetName: action,
            addSheet: action,
            handleSheetTypeSelection: action,
            updateSheetColor: action,
            updateSheetView: action,
        });
    }

    showViewRequirementsModal = (requirements) => {
        this.currentRequirements = Array.isArray(requirements) ? requirements : [];
        this.isViewRequirementsModalOpen = true;
    };

    hideViewRequirementsModal = () => {
        this.isViewRequirementsModalOpen = false;
        this.currentRequirements = null;
    };

    showAddSheetModal = action(() => {
        if (this.visibleSheets.length >= this.MAX_SHEETS) {
            console.warn("Cannot add sheet: Maximum sheet limit reached.");
            return; 
        }
        this.isAddSheetModalOpen = true;
    });

    hideAddSheetModal = action(() => {
        this.isAddSheetModalOpen = false;
    });

    handleAddSheetSelection = action((option) => {
        this.hideAddSheetModal();

        if (this.visibleSheets.length >= this.MAX_SHEETS) return;

        console.log("Handling add sheet selection:", option);

        try {
            switch (option.type) {
                case 'blank':
                    this._addBlankSheet();
                    break;
                case 'view':
                    this._addSheetFromViewType(option.viewId);
                    break;
                case 'template':
                    this._addSheetFromTemplate(option.templateId);
                    break;
                default:
                    console.warn("Unknown add sheet option type:", option.type);
            }
        } catch (error) {
            console.error("Error adding sheet:", error);
        }
    });

    get visibleSheets() {
        return this.stores.sheetStore.visibleSheets || [];
    }

    get selectedSheetIndex() {
        return this.stores.sheetStore.selectedSheetIndex ?? 0;
    }

    get currentSheet() {
        return this.stores.sheetStore.currentSheet;
    }

    addSheet = () => {
        this.showAddSheetModal();
    };

    removeSheet = (index) => {
        if (this.visibleSheets.length <= 1) return;
        this.stores.sheetStore.removeSheet(index);
    };

    selectSheet = (index) => {
        console.log('Selecting sheet:', index);
        this.stores.sheetStore.selectSheet(index);
        console.log('Selected sheet index after update:', this.selectedSheetIndex);
    };

    _getBaseName = (nameWithPotentialEmoji) => {
        if (!nameWithPotentialEmoji) return '';
        const name = nameWithPotentialEmoji.trim();
        const viewEmojis = Object.values(ViewTypes).map(v => v.icon);
        for (const emoji of viewEmojis) {
            if (name.startsWith(emoji + ' ')) {
                return name.substring(emoji.length + 1).trim();
            }
        }
        return name;
    };

    updateSheetName = (index, newNameFromInput) => {
        console.log('[SheetViewModel] updateSheetName called:', { index, newNameFromInput });

        const trimmedNewName = newNameFromInput?.trim();
        if (!trimmedNewName) {
            console.warn("[SheetViewModel] Sheet name cannot be empty after trim. Input:", newNameFromInput);
            return; 
        }

        const sheet = this.visibleSheets[index];
        if (!sheet) {
            console.error(`[SheetViewModel] Sheet at index ${index} not found.`);
            return;
        }
        console.log('[SheetViewModel] Current sheet name:', sheet.name);

        if (trimmedNewName === sheet.name) {
            console.log('[SheetViewModel] No change needed (trimmedNewName === sheet.name). Exiting.');
            return; 
        }

        let desiredEmoji = '';
        let desiredBaseName = trimmedNewName;

        const viewEmojis = Object.values(ViewTypes).map(v => v.icon);
        for (const emoji of viewEmojis) {
            if (trimmedNewName.startsWith(emoji + ' ')) {
                desiredEmoji = emoji;
                desiredBaseName = trimmedNewName.substring(emoji.length + 1).trim();
                break;
            }
        }

        if (desiredEmoji && !desiredBaseName) {
            desiredBaseName = this._getBaseName(sheet.name);
            if (!desiredBaseName) {
                desiredBaseName = "Untitled"; 
            }
        } else if (!desiredBaseName && !desiredEmoji) {
            // This case implies trimmedNewName was empty, but that's caught at the start.
            // Or, if somehow desiredBaseName became empty without an emoji (e.g. from " "),
            // it should also default. This is defensive.
            desiredBaseName = "Untitled";
        }
        
        // Ensure the desiredBaseName is unique among all other sheets' base names
        let uniqueBaseName = desiredBaseName;
        let counter = 1;
        
        const isBaseNameTaken = () =>
            this.visibleSheets.some(s => {
                if (s.id === sheet.id) return false; 
                return this._getBaseName(s.name) === uniqueBaseName;
            });

        // Ensure base name is not empty after all processing, before uniqueness check
        if (!uniqueBaseName.trim()) {
            uniqueBaseName = "Untitled";
        }
        // Re-assign desiredBaseName in case it became "Untitled" for the increment logic
        const originalDesiredBaseNameForIncrement = uniqueBaseName;

        while (isBaseNameTaken()) {
            uniqueBaseName = `${originalDesiredBaseNameForIncrement} (${counter})`;
            counter++;
        }

        let finalName = uniqueBaseName;
        if (desiredEmoji) {
            finalName = `${desiredEmoji} ${uniqueBaseName}`;
        }

        console.log('[SheetViewModel] Calculated finalName:', finalName, "Original sheet.name:", sheet.name);

        if (finalName === sheet.name) {
            console.log('[SheetViewModel] No effective change after processing (finalName === sheet.name). Exiting.');
            return; 
        }
        
        console.log('[SheetViewModel] Calling sheetStore.updateSheetName with:', { sheetId: sheet.id, finalName });
        this.stores.sheetStore.updateSheetName(sheet.id, finalName);
    };

    updateSheetView = action((index, viewId) => {
        const sheet = this.visibleSheets[index];
        if (!sheet) {
            console.error(`Sheet at index ${index} not found for view update.`);
            return;
        }
        const viewType = ViewTypes[viewId];
        if (!viewType) {
            console.error(`Invalid viewId: ${viewId} for sheet view update.`);
            return;
        }

        const currentBaseName = this._getBaseName(sheet.name) || "Untitled";
        const newFullName = `${viewType.icon} ${currentBaseName}`;
        
        // Delegate to updateSheetName for actual name change and uniqueness logic
        this.updateSheetName(index, newFullName);
    });

    handleSheetTypeSelection = (type) => {
        console.log('Handling sheet type selection:', { 
            type, 
            selectedSheetIndex: this.selectedSheetIndex,
            visibleSheets: this.visibleSheets.length
        });
        
        try {
            this.stores.sheetStore.setSheetType(this.selectedSheetIndex, type);
        } catch (error) {
            console.error('Error setting sheet type:', error);
        }
    };

    moveSheet = (fromIndex, toIndex) => {
        console.log('Moving sheet:', { fromIndex, toIndex });
        this.stores.sheetStore.moveSheet(fromIndex, toIndex);
    };

    get availableViews() {
        return Object.values(ViewTypes);
    }

    getDetectedViewType = (sheetName) => {
        if (!sheetName) return 'LIST'; // Default to LIST if no name

        const viewOrder = ['LIST', 'BOARD', 'CALENDAR', 'TIMELINE', 'TABLE']; // Define preference order

        for (const viewId of viewOrder) {
            const viewType = ViewTypes[viewId];
            if (viewType?.icon && sheetName.includes(viewType.icon)) {
                // Check if the view requires specific columns and if the current sheet has them
                const { valid } = this.checkViewRequirements(viewId);
                if (valid) {
                    return viewId; // Return the first valid match based on order
                }
                // If not valid, continue checking other emojis, but keep this as a potential fallback
            }
        }
        
        // If no emoji matched or no matching emoji led to a valid view, default to LIST
        // Check LIST validity specifically before defaulting
        const { valid: listValid } = this.checkViewRequirements('LIST');
        if (listValid) {
            return 'LIST';
        }

        // If even LIST isn't valid, default to TABLE as the ultimate fallback
        return 'TABLE';
    };

    checkViewRequirements(viewType) {
        if (!this.currentSheet || !ViewTypes[viewType]) return { valid: false, missingColumns: [] };
        
        const sheetColumns = this.currentSheet.columns;
        const requiredColumns = ViewTypes[viewType].requiredColumns;
        
        const missingColumns = requiredColumns.filter(required => {
            return !sheetColumns.some(col => col.type.id === required.type.id);
        });

        return {
            valid: missingColumns.length === 0,
            missingColumns
        };
    }

    createRequiredColumns = (viewType) => {
        const { missingColumns } = this.checkViewRequirements(viewType);
        missingColumns.forEach(column => {
            // Assuming you have a method to add columns to the sheet
            this.stores.sheetStore.addColumn(
                this.selectedSheetIndex,
                column.name,
                column.type
            );
        });
    };

    updateSheetColor = (index, color) => {
        console.log(`Setting color for sheet ${index} to ${color}`);
        // Use the store action to update metadata and mark unsaved
        this.stores.sheetStore.updateSheetMetadata(index, { color: color });
    };

    _addBlankSheet = action(() => {
        console.log("[ViewModel:_addBlankSheet] Adding blank sheet...");
        const newSheetInfo = this.stores.sheetStore.addSheet(); 
        if (newSheetInfo) {
            console.log(`[ViewModel:_addBlankSheet] Sheet added by store. Store selected index should be: ${newSheetInfo.index}. Sheet ID: ${newSheetInfo.sheet.id}`);
            // The store's addSheet generates a basic unique name like "Sheet X"
            // If we want a different default or ensure our ViewModel's logic is applied:
            const defaultBaseName = "Sheet"; // Or any other default you prefer
            // updateSheetName expects an index from visibleSheets.
            // After sheetStore.addSheet, newSheetInfo.index is the correct index in the updated visibleSheets.
            this.updateSheetName(newSheetInfo.index, defaultBaseName);
            this.selectSheet(newSheetInfo.index); // Ensure selection if store doesn't guarantee it or if updateSheetName changes index
            console.log(`[ViewModel:_addBlankSheet] Blank sheet processed. Current VM selected index: ${this.selectedSheetIndex}`);
        }
    });

    _addSheetFromViewType = action((viewId) => {
        console.log(`[ViewModel:_addSheetFromViewType] Adding sheet from view type: ${viewId}`);
        const viewType = ViewTypes[viewId];
        if (!viewType) {
            console.error(`[ViewModel:_addSheetFromViewType] Invalid view type ID: ${viewId}`);
            return;
        }

        // Add a sheet with a temporary or no name, let updateSheetName handle the final name.
        const newSheetInfo = this.stores.sheetStore.addSheet(null, { type: viewId });
        if (!newSheetInfo || !newSheetInfo.sheet) {
            console.error("[ViewModel:_addSheetFromViewType] Failed to add sheet via store.");
            return;
        }

        const { index: newSheetIndex, sheet: newSheet } = newSheetInfo;
        console.log(`[ViewModel:_addSheetFromViewType] Sheet added by store. Index: ${newSheetIndex}, ID: ${newSheet.id}`);

        const baseName = viewType.label || "Untitled View";
        const initialName = `${viewType.icon} ${baseName}`;
        
        console.log(`[ViewModel:_addSheetFromViewType] Calling updateSheetName for new sheet. Index: ${newSheetIndex}, Initial Name: "${initialName}"`);
        // updateSheetName will handle uniqueness and apply the name.
        // It takes the index in the *current* visibleSheets array.
        this.updateSheetName(newSheetIndex, initialName);


        if (viewType.requiredColumns && viewType.requiredColumns.length > 0) {
            console.log("[ViewModel:_addSheetFromViewType] Adding required columns:", viewType.requiredColumns);
            // Ensure addColumnsToSheet uses sheet ID
            this.stores.sheetStore.addColumnsToSheet(newSheet.id, viewType.requiredColumns);
        }

        this.selectSheet(newSheetIndex); // Ensure selection
        console.log(`[ViewModel:_addSheetFromViewType] Sheet from view processed. Explicitly selected index: ${newSheetIndex}. Current VM selected index: ${this.selectedSheetIndex}`);
    });

    _addSheetFromTemplate = action((templateId) => {
        console.log(`Adding sheet from template: ${templateId}`);

        // Call the store method which now handles the import and selection
        const newSheetInfo = this.stores.sheetStore.importSheetFromTemplate(templateId);

        if (newSheetInfo) {
             console.log(`[ViewModel:_addSheetFromTemplate] Sheet added from template. Store selected index should be: ${newSheetInfo.index}. Current VM selected index: ${this.selectedSheetIndex}`);
             // Store action already selects the sheet, no need to call this.selectSheet again
        } else {
             console.error(`[ViewModel:_addSheetFromTemplate] Failed to add sheet from template ${templateId}`);
        }
    });
}